import fs from 'node:fs/promises'
import { confirm } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'
import YAML from 'yaml'

import { normalize, upgrade } from '@scalar/openapi-parser'

import { getFileOrUrl, isUrl, isYamlFileName } from '@/domains/document/helpers'
import { output } from '@/helpers/std-out'
import { measureTime } from '@/helpers/time'

export function UpgradeCommand() {
  const cmd = new Command('upgrade')

  cmd.description('Upgrade OpenAPI document to version 3.1')
  cmd.argument('[file|url]', 'File or URL to validate')
  cmd.option('-o, --output <file>', 'Path to save the upgraded output file')
  cmd.action(async (input: string, { output: outputFilePath }) => {
    if (!input) {
      return output
        .error()
        .title('Missing input')
        .message('Please provide a file path or URL to upgrade')
        .exit('error')
    }

    const isUrlInput = isUrl(input)

    if (!outputFilePath) {
      if (isUrlInput) {
        return output
          .error()
          .message(
            'Please provide an output file using the -o flag when upgrading from a URL.',
          )
          .exit('error')
      }

      const shouldContinue = await confirm({
        message:
          'No output file specified - this will overwrite the input file. Continue?',
        initialValue: false,
      })

      if (shouldContinue !== true) {
        return output
          .error()
          .message('Operation cancelled by user')
          .exit('error')
      }
    }

    const outputFileName = outputFilePath ?? input

    const document = normalize(await getFileOrUrl(input))

    const { result, executionTimeMs } = await measureTime(() =>
      upgrade(document),
    )

    const isYaml = isYamlFileName(outputFileName)
    await fs.writeFile(
      outputFileName,
      isYaml
        ? YAML.stringify(result.specification)
        : JSON.stringify(result.specification, null, 2),
    )

    output
      .info()
      .message(
        `${as.green(`✨ OpenAPI Schema upgraded to version ${result.version}`)} in ${as.white(`${as.bold(`${executionTimeMs.toFixed(2)}`)} ms`)}`,
      )
      .print()
  })

  return cmd
}
